/**
 * useApiStore — typed client for the Hono backend.
 *
 * App-scoped Pinia singleton. Owns journey/session orchestration (touch
 * + headers synthesis) and delegates the wire to `@src/api/client.ts`
 * (raw fetch + error classification) and `@src/api/{sessions,surveys}.ts`
 * (per-endpoint payload calls).
 *
 * Session lifecycle: createSession() runs at the start of every test, so
 * sessions and test results are 1:1. The survey (if completed) attaches
 * via `draftId` (24 h client tier) and can span many test sessions and
 * reloads.
 */

import { defineStore } from "pinia";

import { useVariantStore } from "./useVariantStore.ts";
import { useJourneyStore } from "./useJourneyStore.ts";
import { useSessionExpiryStore } from "./useSessionExpiryStore.ts";
import { ProxyUnreachableError } from "@src/api/client.ts";
import * as sessionsApi from "@src/api/sessions.ts";
import * as surveysApi from "@src/api/surveys.ts";

// Re-export so existing consumers that branch on
// `instanceof ProxyUnreachableError` keep their import path. The
// canonical definition lives in api/client.ts.
export { ProxyUnreachableError } from "@src/api/client.ts";

export const useApiStore = defineStore("api", () => {
    const variantStore = useVariantStore();
    const journeyStore = useJourneyStore();
    // The expiry store is the single owner of the server-session
    // substrate. Constructing it here runs the boot-time two-tier TTL
    // sweep and gives us a typed handle for `headers()` reads and
    // `createSession` writes.
    const sessionExpiry = useSessionExpiryStore();

    // ── Helpers ───────────────────────────────────────────────────────

    function headers(): Record<string, string> {
        const h: Record<string, string> = {
            "Content-Type": "application/json",
            "X-Journey-Id": journeyStore.journeyId,
        };
        if (sessionExpiry.sessionToken) {
            h["X-Session-Token"] = sessionExpiry.sessionToken;
        }
        return h;
    }

    /**
     * Touch the journey TTL on every server interaction. The api/* modules
     * are header-agnostic — this function is the journey-touch + header
     * synthesis the store wraps around every call below.
     */
    function withRequest(): Record<string, string> {
        journeyStore.touch();
        return headers();
    }

    // ── Session ───────────────────────────────────────────────────────

    /** Create a new test session. Called at the start of every test. */
    async function createSession(): Promise<string> {
        const { sessionId, sessionActiveUntil } = await sessionsApi.createSession(
            withRequest(),
        );

        // Single atomic write: the expiry store owns token + expiry +
        // localStorage mirror.
        sessionExpiry.setServerSession(
            sessionId,
            new Date(sessionActiveUntil),
        );

        return sessionId;
    }

    // ── Results ───────────────────────────────────────────────────────

    /** Submit a speedtest result. Session must already exist (from createSession).
     *
     *  The server upserts by `sessionId`, so phase-boundary callers send
     *  `status: "partial"` with whichever metrics have completed; the
     *  full-completion call sends `status: "complete"` and the row evolves
     *  in place. Backward `complete → partial` transitions are rejected
     *  server-side. */
    async function submitResult(result: {
        status: "partial" | "complete";
        download?: number | null;
        upload?: number | null;
        ping?: number | null;
        jitter?: number | null;
        raw?: Record<string, unknown>;
    }): Promise<void> {
        const body = {
            ...result,
            variant: variantStore.variant.id,
            journeyId: journeyStore.journeyId,
        };
        if (import.meta.env.DEV) {
            // Keep dev visibility, but still write through to the server.
            console.log("[DEV] Speedtest result (recording):", body);
        }
        await sessionsApi.submitSessionResult(body, withRequest());
    }

    // ── Survey ────────────────────────────────────────────────────────

    /** Submit a survey response. Always carries `draftId`; the server
     *  upserts on `{draftId}` so multiple test sessions and reloads converge
     *  onto a single survey row. */
    async function submitSurvey(
        survey: Record<string, unknown>,
        status: "partial" | "complete",
        draftId: string,
    ): Promise<void> {
        await surveysApi.submitSurvey(
            {
                ...survey,
                status,
                variant: variantStore.variant.id,
                journeyId: journeyStore.journeyId,
                draftId,
            },
            withRequest(),
        );
    }

    /** Resume a saved draft after re-binding a fresh server session.
     *  Posts the restored state as a partial under the existing `draftId`.
     *  Used by App.vue's mount-time auto-resume path. */
    async function replayPartial(
        draftId: string,
        currentState: Record<string, unknown>,
    ): Promise<void> {
        await submitSurvey(currentState, "partial", draftId);
    }

    return {
        createSession,
        submitResult,
        submitSurvey,
        replayPartial,
    };
});
